import type jsPDF from 'jspdf';
import { TEAL_RGB, type LetterheadCustomer } from './letterheadPdf';
import { drawAccentCard, OFFER_MUTED_RGB, OFFER_TEXT_RGB, OFFER_TITLE_RGB } from './offerPdfTheme';

const TOP = 9;
const LOGO_WIDTH = 46;
const MARK_SIZE = 15;
const COLUMN_GAP = 6;
const CARD_PADDING = 4;
const CUSTOMER_LINE_HEIGHT = 3.7;

interface OfferHeaderInput {
  marchioDataUrl: string;
  logotipoDataUrl: string;
  customer: LetterheadCustomer | null | undefined;
  offerName: string;
  dateLabel: string;
}

interface OfferHeaderFrame {
  pageWidth: number;
  margin: number;
}

/** Dati del cliente raccolti su poche righe, per non rubare spazio alla tabella. */
export function offerCustomerLines(customer: LetterheadCustomer | null | undefined): string[] {
  const place = [customer?.postal_code, customer?.city, customer?.province].filter(Boolean).join(' ');
  const joinWithDot = (parts: (string | null | undefined | false)[]): string => parts.filter(Boolean).join('  ·  ');

  return [
    [customer?.address, place].filter(Boolean).join(', '),
    joinWithDot([customer?.vat_number && `P.IVA ${customer.vat_number}`, customer?.tax_code && `C.F. ${customer.tax_code}`]),
    joinWithDot([customer?.email, customer?.phone && `Tel. ${customer.phone}`]),
    customer?.contact_person ? `Referente: ${customer.contact_person}` : '',
  ].filter(Boolean);
}

function drawBrandBar(doc: jsPDF, input: OfferHeaderInput, { pageWidth, margin }: OfferHeaderFrame): number {
  const logo = doc.getImageProperties(input.logotipoDataUrl);
  const logoHeight = (logo.height / logo.width) * LOGO_WIDTH;
  doc.addImage(input.logotipoDataUrl, 'PNG', margin, TOP + (MARK_SIZE - logoHeight) / 2, LOGO_WIDTH, logoHeight);
  doc.addImage(input.marchioDataUrl, 'PNG', pageWidth - margin - MARK_SIZE, TOP, MARK_SIZE, MARK_SIZE);

  const lineY = TOP + MARK_SIZE + 3;
  doc.setDrawColor(...TEAL_RGB);
  doc.setLineWidth(0.5);
  doc.line(margin, lineY, pageWidth - margin, lineY);
  return lineY;
}

function drawTitle(doc: jsPDF, input: OfferHeaderInput, x: number, top: number, width: number): number {
  let y = top + 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(21);
  doc.setTextColor(...OFFER_TITLE_RGB);
  doc.text('Offerta', x, y);

  y += 6.5;
  doc.setFontSize(10.5);
  doc.setTextColor(...TEAL_RGB);
  for (const line of doc.splitTextToSize(input.offerName, width) as string[]) {
    doc.text(line, x, y);
    y += 4.6;
  }

  y += 0.6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...OFFER_MUTED_RGB);
  doc.text(input.dateLabel, x, y);
  return y + 2;
}

function drawCustomerCard(
  doc: jsPDF,
  customer: LetterheadCustomer | null | undefined,
  x: number,
  top: number,
  width: number
): number {
  const textX = x + CARD_PADDING;
  const textWidth = width - 2 * CARD_PADDING;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const details = offerCustomerLines(customer).flatMap((line) => doc.splitTextToSize(line, textWidth) as string[]);
  const height = 13 + details.length * CUSTOMER_LINE_HEIGHT + 1.5;

  drawAccentCard(doc, x, top, width, height);

  let y = top + 4.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...TEAL_RGB);
  doc.text('SPETT.LE', textX, y);

  y += 5;
  doc.setFontSize(10.5);
  doc.setTextColor(...OFFER_TITLE_RGB);
  doc.text(customer?.company_name || 'Cliente', textX, y);

  y += 4.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...OFFER_TEXT_RGB);
  for (const line of details) {
    doc.text(line, textX, y);
    y += CUSTOMER_LINE_HEIGHT;
  }
  return top + height;
}

/**
 * Testata dell'offerta, sulla falsariga delle guide all'offerta: logo a sinistra, marchio a
 * destra, titolo e cliente affiancati. Tiene poco spazio, così in un'offerta corta tabella,
 * condizioni e firme stanno sullo stesso foglio. Restituisce dove può partire la tabella.
 */
export function drawOfferHeader(doc: jsPDF, input: OfferHeaderInput, frame: OfferHeaderFrame): number {
  const columnWidth = (frame.pageWidth - 2 * frame.margin - COLUMN_GAP) / 2;
  const top = drawBrandBar(doc, input, frame) + 5;

  const titleBottom = drawTitle(doc, input, frame.margin, top, columnWidth);
  const cardBottom = drawCustomerCard(doc, input.customer, frame.margin + columnWidth + COLUMN_GAP, top, columnWidth);
  return Math.max(titleBottom, cardBottom) + 5;
}
